import React, { useEffect, useState, useMemo } from "react";
import SummaryApi from "../common";
import { useNavigate } from "react-router-dom";
import displayINRCurrency from "../helpers/displayCurrency";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { FaFileExcel, FaEye, FaEyeSlash } from "react-icons/fa6";
import { FaTimes, FaFileInvoice, FaMoneyBillWave, FaEdit } from "react-icons/fa";
import scrollTop from "../helpers/scrollTop";

const AllStudents = () => {
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const studentsPerPage = 10;
  const navigate = useNavigate();
  const [showTotal, setShowTotal] = useState(false);
  const getTodayDate = () => new Date().toISOString().split("T")[0];
  
  // ---------------- View Student Modal States ----------------
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewStudent, setViewStudent] = useState(null);
  const [viewPaymentHistory, setViewPaymentHistory] = useState([]);
  const [loadingViewPayments, setLoadingViewPayments] = useState(false);
  const [viewPaymentPage, setViewPaymentPage] = useState(1);  // NEW
  const viewPaymentsPerPage = 4;  // NEW
  
  // ---------------- Due Modal States ----------------
  const [showDueModal, setShowDueModal] = useState(false);
  const [dueStudent, setDueStudent] = useState(null);
  const [dueForm, setDueForm] = useState({
    total_fee: "",
    paid_fee: "",
    balance_fee: "",
    payment_date: getTodayDate(),
    due_date: "",
    payment_method: "",
    transaction_id: "",
    remarks: "",
  });
  const [isDueSubmitting, setIsDueSubmitting] = useState(false);
  const [duePaymentList, setDuePaymentList] = useState([]);
  const [loadingDuePayments, setLoadingDuePayments] = useState(false);
  const [duePaymentPage, setDuePaymentPage] = useState(1);
  const duePaymentsPerPage = 4;

  // Auto-reload at 12 AM daily
  useEffect(() => {
    const scheduleReload = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const timeUntilMidnight = tomorrow.getTime() - now.getTime();
      
      setTimeout(() => {
        window.location.reload();
      }, timeUntilMidnight);
    };

    scheduleReload();

    const lastLoadDate = localStorage.getItem('lastLoadDate');
    const today = new Date().toDateString();
    
    if (lastLoadDate !== today) {
      localStorage.setItem('lastLoadDate', today);
      if (lastLoadDate) {
        window.location.reload();
      }
    }

    return () => {
      const timeoutId = setTimeout(() => {}, 0);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await fetch(SummaryApi.getAllStudents.url, {
        method: SummaryApi.getAllStudents.method,
      });
      const result = await response.json();

      if (result.status && Array.isArray(result.data)) {
        setStudents(result.data);
      } else {
        console.error("Unexpected response:", result);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const getLatestPayment = (payments) => {
    if (!payments || payments.length === 0) return null;
    const sorted = [...payments].sort(
      (a, b) => new Date(b.created_date) - new Date(a.created_date)
    );
    return sorted[0];
  };

  const isDueDateExpired = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  const fetchViewPaymentHistory = async (studentId) => {
    setLoadingViewPayments(true);
    try {
      const response = await fetch(SummaryApi.getpaymentallhistory.url, {
        method: SummaryApi.getpaymentallhistory.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ student_id: studentId }),
      });

      const result = await response.json();

      if (result.status && Array.isArray(result.data)) {
        setViewPaymentHistory(result.data);
      } else {
        setViewPaymentHistory([]);
      }
    } catch (error) {
      console.error("Error fetching payment history for view:", error);
      setViewPaymentHistory([]);
    } finally {
      setLoadingViewPayments(false);
    }
  };

  // Open View Student Modal - UPDATED with page reset
  const handleViewStudent = (student) => {
    setViewStudent(student);
    setShowViewModal(true);
    setViewPaymentPage(1);   // NEW - reset page
    fetchViewPaymentHistory(student.student_id);
  };

  const fetchDuePayments = async (studentId) => {
    setLoadingDuePayments(true);
    try {
      const response = await fetch(SummaryApi.getpaymentallhistory.url, {
        method: SummaryApi.getpaymentallhistory.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ student_id: studentId }),
      });

      const result = await response.json();

      if (result.status && Array.isArray(result.data)) {
        setDuePaymentList(result.data);

        const recent = getLatestPayment(result.data);
        if (recent) {
          setDueForm({
            total_fee: recent.balance_fee || "",
            paid_fee: "",
            balance_fee: "",
            payment_date: getTodayDate(),
            due_date: recent.due_date ? recent.due_date.split("T")[0] : "",
            payment_method: recent.payment_method || "",
            transaction_id: "",
            remarks: recent.remarks || "",
          });
        }
      } else {
        setDuePaymentList([]);
      }
    } catch (error) {
      console.error("Error fetching payments for due modal:", error);
      setDuePaymentList([]);
    } finally {
      setLoadingDuePayments(false);
    }
  };

  const handleOpenDueModal = (student) => {
    setDueStudent(student);
    setDuePaymentPage(1);
    setDueForm({
      total_fee: "",
      paid_fee: "",
      balance_fee: "",
      payment_date: getTodayDate(),
      due_date: "",
      payment_method: "",
      transaction_id: "",
      remarks: "",
    });

    if (student.payments && student.payments.length > 0) {
      setDuePaymentList(student.payments);
      const recent = getLatestPayment(student.payments);
      if (recent) {
        setDueForm((prev) => ({
          ...prev,
          total_fee: recent.balance_fee || "",
          due_date: recent.due_date ? recent.due_date.split("T")[0] : "",
          payment_method: recent.payment_method || "",
          remarks: recent.remarks || "",
          payment_date: getTodayDate(),
        }));
      }
    } else {
      setDuePaymentList([]);
    }

    setShowDueModal(true);
    fetchDuePayments(student.student_id);
  };

  const handleDueFormChange = (e) => {
    const { name, value } = e.target;

    const uppercaseFields = ["transaction_id"];
    const updatedValue = uppercaseFields.includes(name)
      ? value.toUpperCase()
      : value;

    const updated = { ...dueForm, [name]: updatedValue };

    if (name === "total_fee" || name === "paid_fee") {
      const totalFee = parseFloat(name === "total_fee" ? updatedValue : dueForm.total_fee) || 0;
      const paidFee = parseFloat(name === "paid_fee" ? updatedValue : dueForm.paid_fee) || 0;
      updated.balance_fee = totalFee - paidFee;

      if (updated.balance_fee <= 0) {
        updated.due_date = "";
      }
    }

    setDueForm(updated);
  };

  const handleDueSubmit = async (e) => {
    e.preventDefault();

    if (isDueSubmitting || !dueStudent) return;

    const totalFee = parseFloat(dueForm.total_fee) || 0;
    const paidFee = parseFloat(dueForm.paid_fee) || 0;
    const balanceFee = totalFee - paidFee;
    const isFullyPaid = balanceFee <= 0;

    if (dueForm.payment_method !== "Cash" && dueForm.transaction_id.trim() === "") {
      toast.error("Transaction ID is required for non-cash payments.");
      return;
    }

    if (!isFullyPaid && dueForm.due_date) {
      const dueDate = new Date(dueForm.due_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dueDate < today) {
        toast.error("Due date cannot be in the past.");
        return;
      }
    }

    if (!isFullyPaid && !dueForm.due_date) {
      toast.error("Due date is required when balance is remaining.");
      return;
    }

    setIsDueSubmitting(true);

    try {
      const paymentData = {
        student_id: dueStudent.student_id,
        total_fee: totalFee,
        paid_fee: paidFee,
        balance_fee: balanceFee,
        payment_date: dueForm.payment_date,
        due_date: isFullyPaid ? "" : dueForm.due_date,
        payment_method: dueForm.payment_method,
        transaction_id: dueForm.transaction_id,
        remarks: dueForm.remarks,
      };

      if (!SummaryApi.paymentCreate) {
        toast.error("Payment API configuration missing");
        setIsDueSubmitting(false);
        return;
      }

      const paymentResponse = await fetch(SummaryApi.paymentCreate.url, {
        method: SummaryApi.paymentCreate.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(paymentData),
      });

      const paymentResult = await paymentResponse.json();

      if (!paymentResult.status) {
        toast.error(paymentResult.message || "Error creating payment");
        setIsDueSubmitting(false);
        return;
      }

      toast.success(paymentResult.message || "Payment recorded successfully!");

      const payload = paymentResult.data || paymentResult.payment || paymentResult;
      let paymentIdToUse = payload.payment_id || payload.id || payload.paymentId || "";

      if (!paymentIdToUse) {
        console.warn("⚠️ No payment_id found in response - fetching from history");
        try {
          const historyResponse = await fetch(SummaryApi.getpaymentallhistory.url, {
            method: SummaryApi.getpaymentallhistory.method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ student_id: dueStudent.student_id }),
          });
          const historyResult = await historyResponse.json();
          if (historyResult.status && historyResult.data && historyResult.data.length > 0) {
            const latestPaymentRecord = historyResult.data[historyResult.data.length - 1];
            paymentIdToUse = latestPaymentRecord.payment_id;
            console.log("✅ Retrieved payment_id from history:", paymentIdToUse);
          }
        } catch (historyError) {
          console.error("Error fetching latest payment for invoice:", historyError);
        }
      }

      if (!paymentIdToUse) {
        paymentIdToUse = Date.now();
        console.warn("⚠️ Using fallback payment_id:", paymentIdToUse);
      }

      window.open(
        `/invoice?student_id=${dueStudent.student_id}&payment_id=${paymentIdToUse}`,
        "_blank"
      );

      await fetchDuePayments(dueStudent.student_id);
      await fetchStudents();

      setDueForm({
        total_fee: balanceFee > 0 ? balanceFee : "",
        paid_fee: "",
        balance_fee: "",
        payment_date: getTodayDate(),
        due_date: isFullyPaid ? "" : dueForm.due_date,
        payment_method: dueForm.payment_method,
        transaction_id: "",
        remarks: "",
      });

      setIsDueSubmitting(false);
    } catch (error) {
      console.error("Due payment error:", error);
      toast.error(error.message || "Error recording payment. Please try again.");
      setIsDueSubmitting(false);
    }
  };

  const handleInvoice = (student, payment) => {
    if (payment) {
      navigate(`/invoice?student_id=${student.student_id}&payment_id=${payment.payment_id}`, {
        state: { student_id: student.student_id, payment_id: payment.payment_id, isHistory: true },
      });
    } else {
      const latestPayment = getLatestPayment(student.payments);
      if (latestPayment) {
        navigate(`/invoice?student_id=${student.student_id}&payment_id=${latestPayment.payment_id}`, {
          state: { student_id: student.student_id, payment_id: latestPayment.payment_id },
        });
      } else {
        navigate("/invoice", { state: { student } });
      }
    }
    scrollTop();
  };

  const handleEditStudent = (student) => {
    navigate("/", { 
      state: { 
        editStudent: student,
        isEditing: true 
      } 
    });
    scrollTop();
  };

  const filteredStudents = useMemo(
    () =>
      students.filter((student) => {
        const searchValue = search.toLowerCase();
        return (
          student.student_admission_number
            ?.toLowerCase()
            .includes(searchValue) ||
          student.mobile_number?.includes(searchValue)
        );
      }),
    [students, search]
  );

  const totalReceivedAmount = useMemo(() => {
    return filteredStudents.reduce((total, student) => {
      const latestPayment = getLatestPayment(student.payments);
      return total + parseFloat(latestPayment?.paid_fee || 0);
    }, 0);
  }, [filteredStudents]);

  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(
    indexOfFirstStudent,
    indexOfLastStudent
  );

  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);

  // View Modal Pagination Calculations - NEW
  const viewTotalPages = Math.ceil(viewPaymentHistory.length / viewPaymentsPerPage);
  const viewIndexOfLast = viewPaymentPage * viewPaymentsPerPage;
  const viewIndexOfFirst = viewIndexOfLast - viewPaymentsPerPage;
  const currentViewPayments = viewPaymentHistory.slice(viewIndexOfFirst, viewIndexOfLast);

  // Due Modal Pagination Calculations
  const dueTotalPages = Math.ceil(duePaymentList.length / duePaymentsPerPage);
  const dueIndexOfLast = duePaymentPage * duePaymentsPerPage;
  const dueIndexOfFirst = dueIndexOfLast - duePaymentsPerPage;
  const currentDuePayments = duePaymentList.slice(dueIndexOfFirst, dueIndexOfLast);

  const handleDownloadExcel = () => {
    const data = filteredStudents.map((student, index) => {
      const latestPayment = getLatestPayment(student.payments);
      return {
        "S.No": index + 1,
        "Transaction ID": latestPayment?.transaction_id || "N/A",
        "Invoice Number": latestPayment?.invoice_number || "N/A",
        "Student Admission Number": student.student_admission_number,
        "Student Name": student.full_name,
        "Father Name": student.father_name,
        Mobile: student.mobile_number,
        Email: student.email_id,
        Course: student.course,
        Year: student.year,
        Semester: student.semester,
        "Total Fee": latestPayment?.total_fee || "N/A",
        "Paid Fee": latestPayment?.paid_fee || "N/A",
        "Balance Fee": latestPayment?.balance_fee || "N/A",
        "Payment Method": latestPayment?.payment_method || "N/A",
        "Payment Date": latestPayment?.payment_date || latestPayment?.created_date || "N/A",
        "Due Date": latestPayment?.due_date || "N/A",
        Remarks: student.remarks || "N/A",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "StudentReports.xlsx");
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00") return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getMinDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 15);
    return date.toISOString().split("T")[0];
  };

  const getMaxDate = () => new Date().toISOString().split("T")[0];
  const getMinDueDate = () => new Date().toISOString().split("T")[0];

  const isSubmitDisabled = isDueSubmitting || 
    (Number(dueForm.total_fee || 0) === 0 && Number(dueForm.balance_fee || 0) === 0);

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold mb-4">All Students</h3>
        <button
          onClick={() => navigate("/")}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm w-full sm:w-auto"
        >
          + Add Student
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-4 w-full">
          <div className="w-full sm:w-96">
            <input
              type="text"
              placeholder="Search by Student Admission Number or Mobile Number"
              className="border p-2 rounded w-full"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <button
          onClick={handleDownloadExcel}
          className="bg-emerald-600 hover:bg-emerald-700 w-48 text-white py-2 px-4 rounded flex justify-center items-center gap-1"
        >
          <FaFileExcel /> Download Excel
        </button>
      </div>

      <div className="overflow-x-auto">
        <div className="h-[600px] overflow-y-auto">
          <table className="min-w-full bg-white border shadow rounded text-sm">
            <thead className="bg-gray-100 text-left sticky top-0 z-10">
              <tr>
                {[
                  "S NO",
                  "Transaction ID",
                  "Invoice Number",
                  "Admission Number",
                  "Full Name",
                  "Father Name",
                  "Mobile Number",
                  "Email",
                  "Course",
                  "Year & Sem",
                  "Total Fee",
                  "Paid Fee",
                  "Balance Fee",
                  "Payment Method",
                  "Payment Date", 
                  "Due Date",
                  "Due",
                  "Actions",
                ].map((head, idx) => (
                  <th
                    key={idx}
                    className="px-4 py-2 border-b whitespace-nowrap bg-gray-100"
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="19" className="text-center py-4 text-gray-500">
                    No students found.
                  </td>
                </tr>
              ) : (
                currentStudents.map((student, index) => {
                  const latestPayment = getLatestPayment(student.payments);
                  const dueStatus = latestPayment?.due_date 
                    ? (isDueDateExpired(latestPayment.due_date) ? "Expired" : "Active")
                    : "N/A";
                  
                  return (
                    <tr
                      key={student.student_id || index}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-4 py-2 border-b">
                        {indexOfFirstStudent + index + 1}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[150px] truncate">
                        {latestPayment?.transaction_id || "N/A"}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[150px] truncate">
                        {latestPayment?.invoice_number || "N/A"}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[150px] truncate">
                        {student.student_admission_number || "N/A"}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[180px] truncate">
                        {student.full_name}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[180px] truncate">
                        {student.father_name}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[120px] truncate">
                        {student.mobile_number}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[200px] truncate">
                        {student.email_id}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[150px] truncate">
                        {student.course}
                      </td>
                      <td className="px-4 py-2 border-b max-w-[150px] truncate">
                        {student.year}-{student.semester} sem
                      </td>
                      <td className="px-4 py-2 border-b">
                        {latestPayment ? displayINRCurrency(latestPayment.total_fee || 0) : "N/A"}
                      </td>
                      <td className="px-4 py-2 border-b text-green-600">
                        {latestPayment ? displayINRCurrency(latestPayment.paid_fee || 0) : "N/A"}
                      </td>
                      <td className="px-4 py-2 border-b text-red-600 font-bold">
                        {latestPayment ? displayINRCurrency(latestPayment.balance_fee || 0) : "N/A"}
                      </td>
                      <td className="px-4 py-2 border-b">
                        {latestPayment?.payment_method || "N/A"}
                      </td>
                      <td className="px-4 py-2 border-b">
                        {latestPayment?.payment_date
                          ? formatDate(latestPayment.payment_date)
                          : (latestPayment?.created_date ? formatDate(latestPayment.created_date) : "N/A")}
                      </td>
                      <td className="px-4 py-2 border-b">
                        {latestPayment?.due_date ? formatDate(latestPayment.due_date) : "N/A"}
                      </td>
                      <td className="px-4 py-2 border-b">
                        <button
                          onClick={() => handleOpenDueModal(student)}
                          className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1 rounded text-xs flex items-center gap-1 w-full justify-center"
                          title="Due"
                        >
                          <FaMoneyBillWave /> Due
                        </button>
                      </td>
                      <td className="px-4 py-2 border-b">
                        <div className="flex flex-wrap gap-1">
                          <button
                            onClick={() => handleViewStudent(student)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs flex items-center gap-1"
                            title="View Details"
                          >
                            <FaEye /> View
                          </button>
                          <button
                            onClick={() => handleEditStudent(student)}
                            className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-xs flex items-center gap-1"
                            title="Edit Student"
                          >
                            <FaEdit /> Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-5">
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 flex-wrap">
              <button
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <div className="text-base font-semibold">
                Page {currentPage} of {totalPages}
              </div>
              <button
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
        <div className="flex justify-between">
          <div className="text-base font-semibold">
            Total Records: <span className="ml-1">{filteredStudents.length}</span>
          </div>
        </div>
      </div>

      {/* View Student Modal - with UPDATED pagination */}
      {showViewModal && viewStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold">
                Student Details - {viewStudent.full_name}
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({viewStudent.student_admission_number})
                </span>
              </h2>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewStudent(null);
                  setViewPaymentHistory([]);
                }}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                <FaTimes />
              </button>
            </div>

            <div className="overflow-y-auto p-6">
              {/* Student Information Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="border rounded-lg p-4">
                  <h3 className="text-sm font-semibold mb-3 text-blue-600 border-b pb-2">
                    Personal Information
                  </h3>
                  <div className="space-y-2">
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Admission Number</label>
                      <p className="text-sm font-semibold">{viewStudent.student_admission_number || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Full Name</label>
                      <p className="text-sm font-semibold">{viewStudent.full_name || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Father Name</label>
                      <p className="text-sm font-semibold">{viewStudent.father_name || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Mobile Number</label>
                      <p className="text-sm font-semibold">{viewStudent.mobile_number || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Email</label>
                      <p className="text-sm font-semibold">{viewStudent.email_id || "N/A"}</p>
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <h3 className="text-sm font-semibold mb-3 text-green-600 border-b pb-2">
                    Academic Information
                  </h3>
                  <div className="space-y-2">
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Course</label>
                      <p className="text-sm font-semibold">{viewStudent.course || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Year</label>
                      <p className="text-sm font-semibold">{viewStudent.year || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Semester</label>
                      <p className="text-sm font-semibold">{viewStudent.semester || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500">Remarks</label>
                      <p className="text-sm font-semibold">{viewStudent.remarks || "N/A"}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment History Section - UPDATED with pagination */}
              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-3 text-purple-600 border-b pb-2">
                  Payment History
                </h3>
                {loadingViewPayments ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    <p className="mt-2 text-gray-500">Loading payment history...</p>
                  </div>
                ) : viewPaymentHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No payment history found for this student.
                  </div>
                ) : (
                  <div className="max-h-[260px] overflow-y-auto">
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 border text-left text-xs">#</th>
                            <th className="px-3 py-2 border text-left text-xs">Invoice No</th>
                            <th className="px-3 py-2 border text-left text-xs">Payment Date</th>
                            <th className="px-3 py-2 border text-left text-xs">Method</th>
                            <th className="px-3 py-2 border text-left text-xs">Total Fee</th>
                            <th className="px-3 py-2 border text-left text-xs">Paid Fee</th>
                            <th className="px-3 py-2 border text-left text-xs">Balance</th>
                            <th className="px-3 py-2 border text-left text-xs">Due Date</th>
                            <th className="px-3 py-2 border text-left text-xs">Transaction ID</th>
                            <th className="px-3 py-2 border text-left text-xs">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentViewPayments.map((payment, idx) => (
                            <tr key={payment.payment_id || idx} className="hover:bg-gray-50">
                              <td className="px-3 py-2 border text-xs">{viewIndexOfFirst + idx + 1}</td>
                              <td className="px-3 py-2 border font-semibold text-xs">
                                {payment.invoice_number || "N/A"}
                              </td>
                              <td className="px-3 py-2 border text-xs">
                                {payment.payment_date ? formatDate(payment.payment_date) : (payment.created_date ? formatDate(payment.created_date) : "N/A")}
                              </td>
                              <td className="px-3 py-2 border text-xs">{payment.payment_method}</td>
                              <td className="px-3 py-2 border text-xs">{displayINRCurrency(payment.total_fee || 0)}</td>
                              <td className="px-3 py-2 border text-xs text-green-600">
                                {displayINRCurrency(payment.paid_fee || 0)}
                              </td>
                              <td className="px-3 py-2 border text-xs text-red-600 font-bold">
                                {displayINRCurrency(payment.balance_fee || 0)}
                              </td>
                              <td className="px-3 py-2 border text-xs">
                                {payment.due_date ? formatDate(payment.due_date) : "N/A"}
                              </td>
                              <td className="px-3 py-2 border text-xs">{payment.transaction_id || "N/A"}</td>
                              <td className="px-3 py-2 border">
                                <button
                                  onClick={() => {
                                    setShowViewModal(false);
                                    handleInvoice(viewStudent, payment);
                                  }}
                                  className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded text-xs flex items-center gap-1"
                                >
                                  <FaFileInvoice /> Invoice
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* NEW - View Modal Pagination Controls */}
                      {viewTotalPages > 1 && (
                        <div className="flex justify-center items-center gap-2 mt-3">
                          <button
                            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                            onClick={() => setViewPaymentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={viewPaymentPage === 1}
                          >
                            Previous
                          </button>
                          <div className="text-sm font-semibold">
                            Page {viewPaymentPage} of {viewTotalPages}
                          </div>
                          <button
                            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                            onClick={() => setViewPaymentPage((prev) => Math.min(prev + 1, viewTotalPages))}
                            disabled={viewPaymentPage === viewTotalPages}
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end p-4 border-t sticky bottom-0 bg-white">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewStudent(null);
                  setViewPaymentHistory([]);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Due Modal - with UPDATED Paid Fee disabled logic */}
      {showDueModal && dueStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold">
                Due - {dueStudent.full_name}
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({dueStudent.student_admission_number})
                </span>
              </h2>
              <button
                onClick={() => setShowDueModal(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                <FaTimes />
              </button>
            </div>

            <div className="overflow-y-auto p-6">
              <form onSubmit={handleDueSubmit} className="grid grid-cols-1 gap-4 bg-gray-50 p-4 rounded border mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block font-medium mb-1">
                      DUE FEE<span className="text-red-500">*</span>
                      <span className="text-xs text-gray-500 ml-2">(Previous Balance)</span>
                    </label>
                    <input
                      type="number"
                      name="total_fee"
              value={dueForm.total_fee === "" ? 0 : dueForm.total_fee}
                      onChange={handleDueFormChange}
                      disabled={Number(dueForm.total_fee || 0) === 0}
                      className={`border p-2 rounded w-full ${
                        Number(dueForm.total_fee || 0) === 0
                          ? "bg-gray-100 cursor-not-allowed"
                          : "bg-yellow-50"
                      }`}
                      min={0}
                      required
                    />
                  </div>
                  
                  {/* UPDATED - Paid Fee with disabled logic when balance_fee === 0 */}
<div>
  <label className="block font-medium mb-1">
    Paid Fee<span className="text-red-500">*</span>
  </label>
  <input
    type="number"
    name="paid_fee"
    value={dueForm.paid_fee}
    onChange={handleDueFormChange}
    disabled={Number(dueForm.total_fee || 0) === 0}
    className={`border p-2 rounded w-full ${
      Number(dueForm.total_fee || 0) === 0
        ? "bg-gray-100 cursor-not-allowed"
        : ""
    }`}
    min={0}
    required
  />
</div>
                  
                  <div>
                    <label className="block font-medium mb-1">Balance Fee</label>
                    <input
                      type="number"
                      value={dueForm.balance_fee}
                      disabled
                      className="border p-2 rounded w-full bg-yellow-100 text-red-700 font-bold"
                    />
                  </div>

                  <div>
                    <label className="block font-medium mb-1">
                      Payment Date<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="payment_date"
                      value={dueForm.payment_date}
                      onChange={handleDueFormChange}
                      className="border p-2 rounded w-full"
                      min={getMinDate()}
                      max={getMaxDate()}
                      required
                    />
                  </div>

                  {Number(dueForm.balance_fee || 0) > 0 && (
                    <div>
                      <label className="block font-medium mb-1">
                        Due Date<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="due_date"
                        value={dueForm.due_date}
                        onChange={handleDueFormChange}
                        className="border p-2 rounded w-full"
                        min={getMinDueDate()}
                        required
                      />
                    </div>
                  )}

                  <div>
                    <label className="block font-medium mb-1">
                      Payment Method<span className="text-red-500">*</span>
                    </label>
                    <select
                      name="payment_method"
                      value={dueForm.payment_method}
                      onChange={handleDueFormChange}
                      className="border p-2 rounded w-full"
                      required
                    >
                      <option value="">Select Payment Method</option>
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                      <option value="UPI">UPI</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-medium mb-1">Transaction ID</label>
                    <input
                      type="text"
                      name="transaction_id"
                      value={dueForm.transaction_id}
                      onChange={handleDueFormChange}
                      className="border p-2 rounded w-full"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block font-medium mb-1">Remarks</label>
                    <textarea
                      name="remarks"
                      value={dueForm.remarks}
                      onChange={handleDueFormChange}
                      className="border p-2 rounded w-full min-h-[42px]"
                      placeholder="Enter any additional notes..."
                    />
                  </div>
                </div>

                <div className="text-right">
                  <button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className={`bg-indigo-600 text-white py-2 px-6 rounded transition flex items-center gap-2 ml-auto ${
                      isSubmitDisabled
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-indigo-700"
                    }`}
                  >
                    {isDueSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>

              <h3 className="text-lg font-semibold mb-2">Existing Payments</h3>
              {loadingDuePayments && duePaymentList.length === 0 ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  <p className="mt-2 text-gray-500">Loading payments...</p>
                </div>
              ) : duePaymentList.length === 0 ? (
                <div className="text-center py-8 text-gray-500 border rounded">
                  No payments found for this student yet.
                </div>
              ) : (
                <div className="max-h-[260px] overflow-y-auto">
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 border text-left">#</th>
                          <th className="px-3 py-2 border text-left">Invoice No</th>
                          <th className="px-3 py-2 border text-left">Payment Date</th>
                          <th className="px-3 py-2 border text-left">Method</th>
                          <th className="px-3 py-2 border text-left">Total Fee</th>
                          <th className="px-3 py-2 border text-left">Paid Fee</th>
                          <th className="px-3 py-2 border text-left">Balance</th>
                          <th className="px-3 py-2 border text-left">Due Date</th>
                          <th className="px-3 py-2 border text-left">Transaction ID</th>
                          <th className="px-3 py-2 border text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentDuePayments.map((payment, idx) => (
                          <tr key={payment.payment_id || idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 border">{dueIndexOfFirst + idx + 1}</td>
                            <td className="px-3 py-2 border font-semibold">
                              {payment.invoice_number || "N/A"}
                            </td>
                            <td className="px-3 py-2 border">
                              {payment.payment_date ? formatDate(payment.payment_date) : (payment.created_date ? formatDate(payment.created_date) : "N/A")}
                            </td>
                            <td className="px-3 py-2 border">{payment.payment_method}</td>
                            <td className="px-3 py-2 border">{displayINRCurrency(payment.total_fee || 0)}</td>
                            <td className="px-3 py-2 border text-green-600">
                              {displayINRCurrency(payment.paid_fee || 0)}
                            </td>
                            <td className="px-3 py-2 border text-red-600 font-bold">
                              {displayINRCurrency(payment.balance_fee || 0)}
                            </td>
                            <td className="px-3 py-2 border">
                              {payment.due_date ? formatDate(payment.due_date) : "N/A"}
                            </td>
                            <td className="px-3 py-2 border">{payment.transaction_id || "N/A"}</td>
                            <td className="px-3 py-2 border">
                              <button
                                onClick={() => handleInvoice(dueStudent, payment)}
                                className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded text-xs flex items-center gap-1"
                              >
                                <FaFileInvoice /> Invoice
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {dueTotalPages > 1 && (
                      <div className="flex justify-center items-center gap-2 mt-3">
                        <button
                          className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                          onClick={() => setDuePaymentPage((prev) => Math.max(prev - 1, 1))}
                          disabled={duePaymentPage === 1}
                        >
                          Previous
                        </button>
                        <div className="text-sm font-semibold">
                          Page {duePaymentPage} of {dueTotalPages}
                        </div>
                        <button
                          className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                          onClick={() => setDuePaymentPage((prev) => Math.min(prev + 1, dueTotalPages))}
                          disabled={duePaymentPage === dueTotalPages}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end p-4 border-t sticky bottom-0 bg-white">
              <button
                onClick={() => setShowDueModal(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllStudents;